import { useQuery } from '@tanstack/react-query';
import { get } from '@/shared/api/api-client';
import type { StaffSpecialityCode } from '@/types/settings-api';

/**
 * Language master search — PROD-761 commit 5.
 *
 * GET /api/Master/LanguageValueSetList?searchQuery={q}
 *
 * Debounced typeahead source for the Professional Details Languages
 * section. Caller is expected to debounce its `searchQuery` input
 * (e.g. via `useDebounce`) — this hook just gates on `searchQuery.length`
 * to avoid hitting the API with an empty query on mount.
 *
 * Response shape verified against live dev API 2026-05-14:
 *   [{ languageId: string, code: { code, displayName, system } }, ...]
 *
 * Example single entry:
 *   { languageId: "be5f807a-...", code: { code: "ENG",
 *     displayName: "English", system: "NAB024" } }
 *
 * `description` and `shortName` on `StaffSpecialityCode` are optional
 * and not returned by this endpoint — TypeScript handles the omission
 * cleanly because both fields are typed `string | null`.
 *
 * Hook returns the array as-is so consumers can map to the SearchPicker
 * `{id, label}` shape inline.
 */
export interface LanguageMasterItem {
  languageId: string;
  code: StaffSpecialityCode;
}

export function useLanguageSearch(searchQuery: string) {
  const trimmed = searchQuery.trim();
  return useQuery({
    queryKey: ['settings', 'master', 'language-search', trimmed],
    queryFn: ({ signal }) => {
      const qs = new URLSearchParams({ searchQuery: trimmed });
      return get<LanguageMasterItem[]>(
        `/api/Master/LanguageValueSetList?${qs.toString()}`,
        { signal },
      );
    },
    enabled: trimmed.length > 0,
    staleTime: 60_000, // master data — cache per query for a minute
  });
}
